import React, { forwardRef, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

export type ValidationState = 'valid' | 'invalid' | 'none';

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
    labelText?: string;
    fieldRequired?: boolean;
    fieldPlaceholder?: string;
    validationState?: ValidationState;
    error?: string | null;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
    ({ labelText, fieldRequired, fieldPlaceholder, validationState = 'none', error = null, className, ...props }, ref) => {
        const [isFocused, setIsFocused] = useState(false);
        const [inputLength, setInputLength] = useState(0);

        useEffect(() => {
            setInputLength(props.value ? String(props.value).length : 0);
        }, [props.value]);
        
        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const field = e.target as HTMLInputElement;
            setInputLength(field.value.length);
        }

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(true);
            props.onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false);
            props.onBlur?.(e);
        };
        return (
            <div className="relative">
                <label className="relative block">
                    <div className="label-container cursor-text">
                        <span className="input-boundary-box relative flex items-center">
                            <span className={`input-container relative flex-1 ${error ? 'error' : ''}`}>
                                <input
                                    ref={ref}
                                    placeholder={isFocused ? fieldPlaceholder : ''}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onInput={handleInputChange}
                                    className={`
                                        w-full min-h-[60px] pl-3 pr-8 pt-6 pb-1
                                        eoe-input bg-gray-200 dark:bg-eoe-dark-input
                                        rounded-2xl
                                        text-gray-800 dark:text-eoe-dark-text
                                        transition-colors
                                        resize-none cursor-text
                                        ${error ? 'text-warning-red dark:text-dark-warning-red' : ''}
                                        ${className || ''}
                                    `}
                                    {...props}
                                />
                                <span 
                                    className={`
                                        absolute left-3
                                        ${isFocused ? 'top-2 text-xs' : inputLength === 0 ? 'top-1/2 -translate-y-1/2' : 'top-2 text-xs'}
                                        transition-all
                                        ${error ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}
                                    `}
                                >
                                    {labelText}
                                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                                </span>
                            </span>
                            {props.maxLength && (
                                <span className="absolute right-3 top-2 text-[10px] text-eoe-text-meta dark:text-eoe-dark-text-meta flex-shrink-0 place-items-end items-end text-right"><span className={`text-[10px] ${error || validationState === 'invalid' ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}`}>{inputLength}</span> / {props.maxLength}
                                </span>
                            )}
                            
                            {validationState !== 'none' && (
                                <span className="absolute right-3 bottom-[20%]">
                                    {validationState === 'valid' && !error ? (
                                        <Check className="h-5 w-5 text-green-600" />
                                    ) : validationState === 'invalid' ? (
                                        <X className="h-5 w-5 text-warning-red dark:text-dark-warning-red" />
                                    ) : null}
                                </span>
                            )}
                        </span>
                    </div>
                </label>
                {error && (
                    <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">{error}</span>
                )}
            </div>
        );
    }
);

InputField.displayName = 'InputField';

export default InputField;